/**
 * Share Customizations
 *
 * This file contains CUSTOM overrides for share behavior.
 * Use this to define event-specific, campaign-specific, or special share configurations.
 *
 * For the core share system (types, defaults, factories), see: src/utils/shareConfig.ts
 * For execution layer (sharing actions, analytics), see: src/utils/shareHelpers.ts
 *
 * Register configurations here using:
 * - registerQuestionConfig() - for question share customizations
 * - registerEventConfig() - for event share customizations
 * - registerProfileConfig() - for profile share customizations
 * - registerAppConfig() - for app-invite share customizations
 */

import {
  registerAppConfig,
  registerQuestionConfig,
  TextPlatform,
  ShareScenario,
} from '@utils/shareHelpers';
import { INVITE_COPY } from './inviteShare';

// =============================================================================
// COFFEE EVENT QUESTION CONFIG
// =============================================================================

const COFFEE_EVENT_ID = '00000000-0000-4000-8000-000000000017';

// When user shares their OWN question from the coffee event
registerQuestionConfig({
  id: 'coffee-event-own-question',
  priority: 100,
  matches: ctx =>
    ctx.eventIds.includes(COFFEE_EVENT_ID) && ctx.scenario === ShareScenario.OwnContent,
  messages: {
    default: ({ url }) => `Hey I just shared that I made coffee, come drink it ${url}`,
    platforms: {
      [TextPlatform.WhatsApp]: ({ url }) =>
        `Hey I just shared that I made coffee, come drink it ${url}`,
    },
    email: {
      subject: () => 'Come have coffee with me!',
      body: ({ url }) =>
        `Hey!\n\nI just shared that I made coffee, come drink it with me.\n\n${url}`,
    },
  },
  ui: {
    title: 'Come have coffee',
    description: 'I just made coffee, come share it with me',
  },
});

// When user shares SOMEONE ELSE'S question from the coffee event
registerQuestionConfig({
  id: 'coffee-event-others-question',
  priority: 100,
  matches: ctx =>
    ctx.eventIds.includes(COFFEE_EVENT_ID) && ctx.scenario === ShareScenario.OthersContent,
  messages: {
    default: ({ url }) =>
      `Someone just made coffee and is looking for company! Check it out ${url}`,
    platforms: {
      [TextPlatform.WhatsApp]: ({ url }) =>
        `Hey! Someone just made coffee and wants to share ☕ Check it out: ${url}`,
    },
    email: {
      subject: () => 'Someone made coffee!',
      body: ({ url }) =>
        `Hey!\n\nSomeone just made coffee and is looking for company. You should check it out!\n\n${url}`,
    },
  },
  ui: {
    title: 'Share this coffee moment ☕',
    description: 'Help them find someone to share coffee with',
  },
});

// =============================================================================
// APP INVITE CONFIG
// =============================================================================

// Default App invite config - the single config for the App entity.
// Drawer titles resolve via the copy-experiment bucket in shareConfig's App
// defaults (no ui override here); every string lives in
// src/config/inviteShare.ts (INVITE_COPY).
registerAppConfig({
  id: 'app-invite-default',
  priority: 0,
  matches: () => true,
  messages: {
    default: ({ url }) => INVITE_COPY.shareMessage(url),
    email: {
      subject: () => INVITE_COPY.emailSubject,
      body: ({ url }) => INVITE_COPY.shareMessage(url),
    },
  },
});
